import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, MessageCircle, Mail, Phone } from "lucide-react";
import { SEDI_NAMES, COURSE_OPTIONS, PROMO, CONTACTS, waLink } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  nome: "",
  cognome: "",
  email: "",
  telefono: "",
  sede: "",
  corso: "",
  note: "",
};

const inputCls =
  "w-full rounded-xl bg-ink-900 border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors duration-200";

export const PreEnroll = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email || !form.telefono || !form.sede || !form.corso) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/preiscrizione`, form);
      setDone(true);
      toast.success("Richiesta inviata! Ti contatteremo a breve.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" className="relative py-24 sm:py-32 bg-ink-800/40">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        {/* Left pitch */}
        <div>
          <h4 className="text-xs uppercase tracking-[0.3em] text-gold-500 font-sans mb-5">
            Iscrizioni A.S. {PROMO.schoolYear}
          </h4>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-white leading-tight">
            Contattaci
            <br />
            <span className="italic text-gold-500">per informazioni.</span>
          </h2>
          <p className="mt-6 text-base text-gray-400 max-w-md">
            Iscrizioni aperte per il nuovo anno. Scrivici via WhatsApp, email o
            compila il modulo: ti ricontattiamo per fissare la prima lezione
            conoscitiva gratuita. Inizio lezioni il{" "}
            <span className="text-white font-medium">{PROMO.startLabel}</span>.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="contatti-whatsapp-btn"
              href={waLink("Ciao, vorrei informazioni sui corsi e le iscrizioni 2026/2027 di Trinity Musical School")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-medium px-6 py-3 hover:-translate-y-0.5 transition-transform duration-200"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
            <a
              data-testid="contatti-email-btn"
              href={`mailto:${CONTACTS.email}?subject=Richiesta informazioni corsi 2026/2027`}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white px-6 py-3 hover:border-gold-500 hover:text-gold-500 transition-colors duration-200"
            >
              <Mail size={18} /> Email
            </a>
            <a
              data-testid="contatti-phone-btn"
              href={`tel:${CONTACTS.mobiles[1].link}`}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white px-6 py-3 hover:border-gold-500 hover:text-gold-500 transition-colors duration-200"
            >
              <Phone size={18} /> Chiama
            </a>
          </div>

          <ul className="mt-8 space-y-3">
            {[
              "Risposta entro poche ore",
              "Prima lezione conoscitiva gratuita",
              "Inizio lezioni: 3 settembre 2026",
              "Quote detraibili nel 730",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-gray-300">
                <CheckCircle2 size={18} className="text-gold-500 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Form card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-white/10 bg-ink-800 p-7 sm:p-9"
        >
          {done ? (
            <div data-testid="preiscrizione-success" className="text-center py-12">
              <CheckCircle2 size={56} className="text-gold-500 mx-auto" />
              <h3 className="mt-6 font-serif text-3xl text-white">Grazie!</h3>
              <p className="mt-3 text-gray-400">
                La tua richiesta è stata inviata. Ti contatteremo a breve per la
                prima lezione conoscitiva.
              </p>
              <button
                data-testid="preiscrizione-reset-btn"
                onClick={() => setDone(false)}
                className="mt-8 rounded-full border border-white/20 text-white px-6 py-3 hover:border-gold-500 hover:text-gold-500 transition-colors duration-200"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form data-testid="preiscrizione-form" onSubmit={submit} className="space-y-4">
              <h3 className="font-serif text-2xl text-white mb-2">Richiedi informazioni</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Nome *</label>
                  <input
                    data-testid="input-nome"
                    className={inputCls}
                    placeholder="Mario"
                    value={form.nome}
                    onChange={set("nome")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Cognome *</label>
                  <input
                    data-testid="input-cognome"
                    className={inputCls}
                    placeholder="Rossi"
                    value={form.cognome}
                    onChange={set("cognome")}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Email *</label>
                  <input
                    data-testid="input-email"
                    type="email"
                    className={inputCls}
                    placeholder="mario@email.it"
                    value={form.email}
                    onChange={set("email")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Telefono *</label>
                  <input
                    data-testid="input-telefono"
                    className={inputCls}
                    placeholder="+39 333 1234567"
                    value={form.telefono}
                    onChange={set("telefono")}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Sede preferita *</label>
                  <select
                    data-testid="select-sede"
                    className={inputCls}
                    value={form.sede}
                    onChange={set("sede")}
                  >
                    <option value="">Scegli...</option>
                    {SEDI_NAMES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-2">Corso di interesse *</label>
                  <select
                    data-testid="select-corso"
                    className={inputCls}
                    value={form.corso}
                    onChange={set("corso")}
                  >
                    <option value="">Scegli...</option>
                    {COURSE_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-2">Messaggio (facoltativo)</label>
                <textarea
                  data-testid="input-note"
                  rows={3}
                  className={inputCls}
                  placeholder="Età dell'allievo, esperienza pregressa, domande..."
                  value={form.note}
                  onChange={set("note")}
                />
              </div>

              <button
                data-testid="preiscrizione-submit-btn"
                type="submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 text-ink-900 font-semibold py-4 hover:bg-gold-400 transition-colors duration-200 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Invio in corso...
                  </>
                ) : (
                  <>
                    Invia richiesta
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
                  </>
                )}
              </button>
              <p className="text-center text-xs text-gray-500">
                Inviando accetti di essere ricontattato. I tuoi dati saranno trattati
                secondo la Privacy Policy.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
